<script lang="ts">
	import { browser } from '$app/environment';
	import { onMount } from 'svelte';
	import type { User } from '$lib/types/user';
	import MarketingHeader from '$lib/components/marketing/MarketingHeader.svelte';
	import MarketingFooter from '$lib/components/marketing/MarketingFooter.svelte';
	import '$styles/modern.css'; // This one can remain global as it's used app-wide
	// Import marketing styles in the component scope - should override modern styles
	import '$lib/styles/marketing-scoped.css';

	let { title, user, children } = $props<{
		title: string;
		user?: User;
		children: () => import('svelte').Snippet;
	}>();

	// Initialize with system preference for SSR
	let darkMode = $state(false);
	let userHasSetPreference = $state(false);

	onMount(() => {
		try {
			// Check for saved user preference first
			const savedPreference = localStorage.getItem('marketing-dark-mode');
			const savedUserPreference = localStorage.getItem('marketing-dark-mode-set');

			if (savedUserPreference === 'true' && savedPreference !== null) {
				// User has explicitly set a preference
				darkMode = savedPreference === 'true';
				userHasSetPreference = true;
			} else {
				// No user preference, use system preference
				darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
				userHasSetPreference = false;
			}
		} catch {
			// localStorage not available, fall back to system preference
			darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
			userHasSetPreference = false;
		}

		// Apply the initial state
		updateDarkMode(darkMode);

		// Listen for system preference changes if user hasn't set a preference
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = (e: MediaQueryListEvent) => {
			if (!userHasSetPreference) {
				darkMode = e.matches;
				updateDarkMode(darkMode);
			}
		};

		mediaQuery.addEventListener('change', handleChange);

		return () => {
			mediaQuery.removeEventListener('change', handleChange);
		};
	});

	function updateDarkMode(isDark: boolean) {
		if (browser) {
			if (isDark) {
				document.documentElement.classList.add('dark-mode-override');
			} else {
				document.documentElement.classList.remove('dark-mode-override');
			}
			// Only save if user has explicitly set a preference
			if (userHasSetPreference) {
				try {
					localStorage.setItem('marketing-dark-mode', String(isDark));
				} catch {
					// localStorage not available, silently fail
				}
			}
		}
	}

	function toggleDarkMode() {
		darkMode = !darkMode;
		userHasSetPreference = true;
		try {
			localStorage.setItem('marketing-dark-mode-set', 'true');
		} catch {
			// localStorage not available, but still update the UI
		}
		updateDarkMode(darkMode);
	}

	$effect(() => {
		if (!browser) return;

		// Initialize Google Analytics
		const _gaq = window._gaq || [];
		_gaq.push(['_setAccount', 'UA-85339-4']);
		_gaq.push(['_trackPageview']);

		const ga = document.createElement('script');
		ga.type = 'text/javascript';
		ga.async = true;
		ga.src = 'https://www.google-analytics.com/ga.js';
		(
			document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]
		).appendChild(ga);
	});
</script>

<svelte:head>
	<title>{title}</title>
	<meta
		name="keywords"
		content="farmers market software,online farmers market,local food marketplace,farm to table,community supported agriculture,CSA management"
	/>
	<meta
		name="description"
		content="LocallyGrown.net - The leading online farmers market platform. Connect local farmers with their community through our easy-to-use marketplace system. Start your market today!"
	/>
	<link rel="icon" href="/favicon.ico" type="image/x-icon" />
	<!-- Prevent dark mode flicker -->
	{@html `
		<script>
			(function() {
				try {
					// Check if localStorage is available
					const storage = window.localStorage;
					const savedPreference = storage.getItem('marketing-dark-mode');
					const savedUserPreference = storage.getItem('marketing-dark-mode-set');

					if (savedUserPreference === 'true' && savedPreference !== null) {
						// User has explicitly set a preference
						if (savedPreference === 'true') {
							document.documentElement.classList.add('dark-mode-override');
						}
						// If savedPreference === 'false', we explicitly want light mode
						// so we don't add the dark-mode-override class
					} else {
						// No user preference or invalid state, follow system preference
						if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
							document.documentElement.classList.add('dark-mode-override');
						}
					}
				} catch {
					// localStorage not available (e.g., private browsing mode)
					// Fall back to system preference only
					if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
						document.documentElement.classList.add('dark-mode-override');
					}
				}
			})();
		</script>
	`}
</svelte:head>

<div class="marketing-wrapper marketing-page-scope">
	<!-- Marketing Header -->
	<MarketingHeader {user} {darkMode} onToggleDarkMode={toggleDarkMode} />

	<!-- Main Content -->
	<main class="marketing-content">
		{@render children()}
	</main>

	<!-- Marketing Footer -->
	<MarketingFooter />
</div>

<style>
	.marketing-wrapper {
		min-height: 100vh;
		min-height: 100dvh; /* Use dynamic viewport height on supported browsers */
		display: flex;
		flex-direction: column;
		background-color: var(--marketing-bg-white);
		color: var(--marketing-text-primary);
	}

	.marketing-content {
		flex: 1;
		background-color: var(--marketing-bg-white);
	}

	@media (max-width: 768px) {
		.marketing-wrapper {
			min-height: 100vh;
			min-height: 100svh; /* Use small viewport height for mobile to avoid address bar issues */
		}
	}
</style>
